// src/pages/ModelDashboard.js

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { motion } from 'framer-motion';
import EngagementMetrics from '../components/EngagementMetrics';
import BookingGrid from '../components/BookingGrid';

export default function ModelDashboard() {
  const { username } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modelId, setModelId] = useState(null);
  const [modelData, setModelData] = useState(null);
  const [bookingStats, setBookingStats] = useState({
    today: 0,
    total: 0,
    pending: 0,
    confirmed: 0
  });
  const [estimatedRevenue, setEstimatedRevenue] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Buscar o ID da modelo no Firestore
        const modelsRef = collection(db, 'models');
        const q = query(modelsRef, where('username', '==', username));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0];
          const data = doc.data();
          console.log('Model data found:', { id: doc.id, ...data });
          setModelId(doc.id);
          setModelData(data);

          // Buscar estatísticas de agendamentos
          const bookingsRef = collection(db, 'bookings');
          const bookingsQuery = query(bookingsRef, where('username', '==', username));
          const bookingsSnapshot = await getDocs(bookingsQuery);
          
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const stats = {
            today: 0,
            total: bookingsSnapshot.size,
            pending: 0,
            confirmed: 0
          };

          bookingsSnapshot.forEach(doc => {
            const booking = doc.data();
            const bookingDate = booking.date?.date ? new Date(booking.date.date.seconds * 1000) : null;
            
            if (bookingDate && bookingDate >= today) {
              stats.today++;
            }
            
            if (booking.status === 'new' || booking.status === 'pending') {
              stats.pending++;
            } else if (booking.status === 'confirmed') {
              stats.confirmed++;
            }
          });

          setBookingStats(stats);
          
          // Calculate estimated revenue
          if (data.price) {
            setEstimatedRevenue(stats.confirmed * data.price);
          }
        } else {
          console.error('No model found with username:', username);
          setError('Modelo não encontrado');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching model data:', error);
        setError('Erro ao carregar dados');
        setLoading(false);
      }
    };

    if (username) {
      fetchData();
    }
  }, [username]);

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 py-8"
      >
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 leading-relaxed">Dashboard de {username}</h1>
            <p className="text-gray-600 text-lg leading-relaxed">Carregando dados...</p>
          </div>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="h-20 bg-gray-200 rounded"></div>
              <div className="h-20 bg-gray-200 rounded"></div>
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="min-h-screen bg-gray-50 flex items-center justify-center p-6"
      >
        <div className="bg-white rounded-2xl shadow-md p-6 sm:p-8 text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-gray-900">Erro</h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 py-8"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 leading-relaxed">
            Dashboard de {modelData?.displayName || username}
          </h1>
          <p className="text-gray-600 text-lg leading-relaxed">Bem-vindo ao seu painel de controle</p>
        </div>

        {/* Métricas de Engajamento */}
        {modelId && (
          <div className="mb-8">
            <EngagementMetrics modelId={modelId} />
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-8">
          {/* Revenue Card - More prominent */}
          <div className="bg-gradient-to-br from-[#e91e63] to-[#ff4081] rounded-2xl shadow-lg p-6 col-span-1 sm:col-span-2 md:col-span-4">
            <h3 className="text-lg font-medium text-white">Faturamento Estimado</h3>
            <div className="mt-2 flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">R$</span>
                </div>
                <span className="ml-4 text-3xl font-bold text-white">
                  {estimatedRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <p className="text-white/80 text-sm">Baseado em {bookingStats.confirmed} agendamentos confirmados</p>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900">Agendamentos Hoje</h3>
            <div className="mt-2 flex items-center">
              <div className="w-12 h-12 bg-[#e91e63]/10 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-[#e91e63]">{bookingStats.today}</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900">Total de Agendamentos</h3>
            <div className="mt-2 flex items-center">
              <div className="w-12 h-12 bg-[#e91e63]/10 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-[#e91e63]">{bookingStats.total}</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900">Agendamentos Pendentes</h3>
            <div className="mt-2 flex items-center">
              <div className="w-12 h-12 bg-[#e91e63]/10 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-[#e91e63]">{bookingStats.pending}</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900">Agendamentos Confirmados</h3>
            <div className="mt-2 flex items-center">
              <div className="w-12 h-12 bg-[#e91e63]/10 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-[#e91e63]">{bookingStats.confirmed}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bookings Area */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Novos Agendamentos</h2>
          <BookingGrid username={username} />
        </div>
      </div>
    </motion.div>
  );
}